import { ZuruflexApiError } from '@zuruflex/api-client';
import { reviewContent } from '@zuruflex/core';
import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import {
  contentPath,
  EMPTY_CONTENT_DOC,
  PARTNER_TERMS_SLUG,
  statusOf,
  type ContentDoc,
  type ContentPageAdmin,
} from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  ContentDocument,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  FormField,
  Skeleton,
  Switch,
  Textarea,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, ExternalLink, Eye, Pencil, Save, Send } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useBlocker, useNavigate, useParams } from 'react-router-dom';
import { ContentEditor } from '../components/content/ContentEditor.js';
import { PublishDialog } from '../components/content/PublishDialog.js';
import { VersionsPanel } from '../components/content/VersionsPanel.js';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { STATUS_VARIANT, webUrl } from '../lib/content.js';

interface Form {
  title: string;
  lead: string;
  summary: string;
  noindex: boolean;
  doc: ContentDoc;
}

const fromPage = (page: ContentPageAdmin): Form => {
  // The working copy if there is one, otherwise what is live: editing a
  // published page starts from what readers see.
  const source = page.draft ?? page.published;
  return {
    title: source?.title ?? '',
    lead: source?.lead ?? '',
    summary: page.summary ?? '',
    noindex: page.noindex,
    doc: source?.doc ?? EMPTY_CONTENT_DOC,
  };
};

const snapshot = (form: Form) => JSON.stringify(form);

/**
 * The full-page editor.
 *
 * Sits outside the dashboard's chrome — no sidebar, the whole viewport is
 * the page — because writing a policy is not done in a 900-pixel column.
 * Saving only ever touches the draft; the live page changes when an owner
 * publishes.
 *
 * The editor owns its content once mounted, so the form is reset from the
 * server only at moments that change what is being edited: first load, a
 * discard, a publish, or a reload after somebody else saved first. An
 * ordinary save does not reset it, or the caret would jump to the top.
 */
export function ContentEditorPage() {
  const { slug = '' } = useParams();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const canPublish = user?.role === 'OWNER';

  const { data: page, error, refetch } = useQuery({
    queryKey: ['content', slug],
    queryFn: () => api.contentAdminGet(slug),
    enabled: canEdit,
    // The form is the source of truth while somebody is typing.
    refetchOnWindowFocus: false,
  });

  const [form, setForm] = useState<Form | null>(null);
  const [documentKey, setDocumentKey] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [confirm, setConfirm] = useState<'publish' | 'discard' | 'unpublish' | 'delete' | null>(null);
  const [notice, setNotice] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);
  const saved = useRef('');
  const resets = useRef(0);

  /** Replaces the form with what the server holds. */
  const load = useCallback((next: ContentPageAdmin) => {
    const value = fromPage(next);
    saved.current = snapshot(value);
    resets.current += 1;
    setForm(value);
    setDocumentKey(`${next.slug}:${resets.current}`);
  }, []);

  useEffect(() => {
    if (page && form === null) load(page);
  }, [page, form, load]);

  const dirty = form !== null && snapshot(form) !== saved.current;

  const accept = (next: ContentPageAdmin, options: { reset: boolean }) => {
    queryClient.setQueryData(['content', slug], next);
    void queryClient.invalidateQueries({ queryKey: ['content'], exact: true });
    void queryClient.invalidateQueries({ queryKey: ['content', slug, 'history'] });
    void queryClient.invalidateQueries({ queryKey: ['content', slug, 'revisions'] });
    if (options.reset) load(next);
  };

  const save = useMutation({
    mutationFn: (value: Form) =>
      api.saveContentDraft(slug, {
        title: value.title.trim() || slug,
        lead: value.lead.trim() || null,
        summary: value.summary.trim() || null,
        noindex: value.noindex,
        doc: value.doc,
        expectedVersion: page!.version,
      }),
    onSuccess: (next, value) => {
      // Only what was sent is now saved; anything typed since stays dirty.
      saved.current = snapshot(value);
      accept(next, { reset: false });
      setNotice(null);
    },
  });

  const publish = useMutation({
    mutationFn: async () => {
      let current = page!;
      if (dirty && form) {
        current = await api.saveContentDraft(slug, {
          title: form.title.trim() || slug,
          lead: form.lead.trim() || null,
          summary: form.summary.trim() || null,
          noindex: form.noindex,
          doc: form.doc,
          expectedVersion: current.version,
        });
      }
      return api.publishContent(slug, current.version);
    },
    onSuccess: (next) => {
      accept(next, { reset: true });
      setConfirm(null);
      setNotice({
        variant: 'success',
        text: t('content.editor.published', { version: next.published?.version ?? 0 }),
      });
    },
  });

  const schedule = useMutation({
    mutationFn: async (at: string) => {
      let current = page!;
      if (dirty && form) {
        current = await api.saveContentDraft(slug, {
          title: form.title.trim() || slug,
          lead: form.lead.trim() || null,
          summary: form.summary.trim() || null,
          noindex: form.noindex,
          doc: form.doc,
          expectedVersion: current.version,
        });
      }
      return api.scheduleContent(slug, at, current.version);
    },
    onSuccess: (next) => {
      accept(next, { reset: false });
      saved.current = form ? snapshot(form) : saved.current;
      setConfirm(null);
      setNotice({
        variant: 'success',
        text: t('content.schedule.done', {
          when: next.scheduledFor ? formatDateTimeIn(next.scheduledFor, locale) : '',
        }),
      });
    },
  });

  const unschedule = useMutation({
    mutationFn: () => api.unscheduleContent(slug, page!.version),
    onSuccess: (next) => accept(next, { reset: false }),
  });

  const discard = useMutation({
    mutationFn: () => api.discardContentDraft(slug, page!.version),
    onSuccess: (next) => {
      accept(next, { reset: true });
      setConfirm(null);
    },
  });

  const unpublish = useMutation({
    mutationFn: () => api.unpublishContent(slug, page!.version),
    onSuccess: (next) => {
      accept(next, { reset: false });
      setConfirm(null);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteContent(slug),
    onSuccess: () => {
      // Drop the deleted page's queries first, or leaving this screen refetches it into a 404.
      queryClient.removeQueries({ queryKey: ['content', slug] });
      void queryClient.invalidateQueries({ queryKey: ['content'], exact: true });
      navigate('/content');
    },
  });

  // Which pages are on the site, so the checks can tell a link to one from a typo.
  const siblings = useQuery({
    queryKey: ['content'],
    queryFn: () => api.contentAdminList(),
    enabled: canEdit,
  });
  const warnings = useMemo(() => {
    if (!form) return [];
    const livePaths = (siblings.data ?? [])
      .filter((row) => row.publishedVersion > 0 && row.status !== 'UNPUBLISHED')
      .map((row) => contentPath(row.kind, row.slug));
    return reviewContent(form.doc, livePaths);
  }, [form, siblings.data]);

  const history = useQuery({
    queryKey: ['content', slug, 'history'],
    queryFn: () => api.contentHistory(slug),
    enabled: canEdit && !!page,
  });

  const trySave = useCallback(() => {
    if (form && dirty && !save.isPending) save.mutate(form);
  }, [form, dirty, save]);

  // ⌘S / Ctrl+S saves the draft.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        trySave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [trySave]);

  // Leaving with unsaved work asks first — in-app and on closing the tab.
  const blocker = useBlocker(dirty);
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const status = useMemo(
    () =>
      page
        ? statusOf({
            hasDraft: page.draft !== null,
            publishedVersion: page.published?.version ?? 0,
            isLive: page.isLive,
            scheduled: page.scheduledFor !== null,
          })
        : null,
    [page],
  );

  if (!canEdit) {
    return (
      <div className="mx-auto max-w-lg p-8">
        <Alert variant="info">{t('content.editor.noAccess')}</Alert>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-lg p-8">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }
  if (!page || !form || !status) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-8">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  const publicHref = webUrl ? `${webUrl}${contentPath(page.kind, page.slug)}` : null;
  const mutationError =
    save.error ?? publish.error ?? schedule.error ?? unschedule.error ?? discard.error ?? unpublish.error ?? remove.error;
  const conflict = mutationError instanceof ZuruflexApiError && mutationError.status === 409;
  const isAgreement = page.slug === PARTNER_TERMS_SLUG;
  const nextVersion = (page.published?.version ?? 0) + 1;

  const patch = (partial: Partial<Form>) => setForm((current) => (current ? { ...current, ...partial } : current));

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border-default bg-surface px-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/content">
            <ArrowLeft />
            {t('content.title')}
          </Link>
        </Button>
        <div className="hidden min-w-0 items-center gap-2 sm:flex">
          <span className="truncate font-mono text-xs text-text-muted">{page.slug}</span>
          <Badge variant={STATUS_VARIANT[status]}>{t(`content.status.${status}`)}</Badge>
          {page.published ? <Badge variant="outline">v{page.published.version}</Badge> : null}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="hidden text-xs text-text-muted md:block" aria-live="polite">
            {save.isPending
              ? t('content.editor.saving')
              : dirty
                ? t('content.editor.unsaved')
                : (
                    <span className="inline-flex items-center gap-1">
                      <Check className="size-3.5" />
                      {page.draft
                        ? t('content.editor.draftSaved', { when: formatDateTimeIn(page.draft.updatedAt, locale) })
                        : page.isLive
                          ? t('content.editor.upToDate')
                          : t('content.editor.offline')}
                    </span>
                  )}
          </span>
          <Button variant="outline" size="sm" onClick={() => setPreview((value) => !value)}>
            {preview ? <Pencil /> : <Eye />}
            {preview ? t('content.editor.edit') : t('content.editor.preview')}
          </Button>
          <Button variant="outline" size="sm" disabled={!dirty} isLoading={save.isPending} onClick={trySave}>
            <Save />
            {t('content.editor.save')}
          </Button>
          {canPublish ? (
            <Button
              size="sm"
              disabled={!dirty && page.draft === null}
              onClick={() => setConfirm('publish')}
            >
              <Send />
              {t('content.editor.publish')}
            </Button>
          ) : null}
        </div>
      </header>

      {mutationError || notice ? (
        <div className="mx-auto w-full max-w-6xl px-4 pt-4">
          {mutationError ? (
            <Alert variant={conflict ? 'warning' : 'danger'}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span>
                  {mutationError instanceof ZuruflexApiError
                    ? mutationError.message
                    : t('common:error.unexpected')}
                </span>
                {conflict ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      const fresh = (await refetch()).data;
                      if (fresh) load(fresh);
                      save.reset();
                      publish.reset();
                      discard.reset();
                      unpublish.reset();
                    }}
                  >
                    {t('content.editor.reload')}
                  </Button>
                ) : null}
              </div>
            </Alert>
          ) : (
            <Alert variant={notice?.variant ?? 'success'}>{notice?.text}</Alert>
          )}
        </div>
      ) : null}

      <div className="mx-auto grid w-full max-w-6xl flex-1 gap-8 px-4 py-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <main className="min-w-0">
          {preview ? (
            <article className="mx-auto max-w-3xl">
              <h1 className="font-display text-4xl font-bold tracking-tight">{form.title}</h1>
              {form.lead ? <p className="mt-3 text-lg text-text-muted">{form.lead}</p> : null}
              <ContentDocument doc={form.doc} className="mt-8" />
            </article>
          ) : (
            <div className="mx-auto max-w-3xl">
              <input
                value={form.title}
                onChange={(event) => patch({ title: event.target.value })}
                aria-label={t('content.editor.titleLabel')}
                placeholder={t('content.editor.titlePlaceholder')}
                className="w-full bg-transparent font-display text-4xl font-bold tracking-tight outline-none placeholder:text-text-muted/50"
              />
              <textarea
                value={form.lead}
                onChange={(event) => patch({ lead: event.target.value })}
                aria-label={t('content.editor.leadLabel')}
                placeholder={t('content.editor.leadPlaceholder')}
                rows={2}
                className="mt-3 mb-8 w-full resize-none bg-transparent text-lg text-text-muted outline-none placeholder:text-text-muted/50"
              />
              <ContentEditor
                doc={form.doc}
                documentKey={documentKey}
                editable
                page={{ title: form.title, kind: page.kind }}
                onChange={(doc) => patch({ doc })}
                onUpload={async (file) => (await api.uploadContentAsset(file)).url}
                onError={(text) => setNotice({ variant: 'danger', text })}
              />
            </div>
          )}
        </main>

        <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start">
          {page.scheduledFor ? (
            <section className="space-y-2 rounded-2xl border border-accent bg-accent/10 p-4 text-sm">
              <h2 className="font-semibold">{t('content.schedule.scheduled')}</h2>
              <p>
                {t('content.schedule.goesLive', { when: formatDateTimeIn(page.scheduledFor, locale) })}
                {page.scheduledByName ? ` · ${page.scheduledByName}` : ''}
              </p>
              <p className="text-xs text-text-muted">{t('content.schedule.editCancels')}</p>
              {canPublish ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  isLoading={unschedule.isPending}
                  onClick={() => unschedule.mutate()}
                >
                  {t('content.schedule.cancel')}
                </Button>
              ) : null}
            </section>
          ) : null}

          <section className="space-y-3 rounded-2xl border border-border-default p-4">
            <h2 className="text-sm font-semibold">{t('content.panel.page')}</h2>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-text-muted">{t('content.column.kind')}</dt>
                <dd>{t(`content.kind.${page.kind}`)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-text-muted">{t('content.panel.address')}</dt>
                <dd className="truncate font-mono text-xs">{contentPath(page.kind, page.slug)}</dd>
              </div>
              {page.published ? (
                <div className="flex justify-between gap-2">
                  <dt className="text-text-muted">{t('content.panel.live')}</dt>
                  <dd className="text-right">
                    {formatDateTimeIn(page.published.publishedAt, locale)}
                    {page.published.publishedByName ? (
                      <span className="block text-xs text-text-muted">{page.published.publishedByName}</span>
                    ) : null}
                  </dd>
                </div>
              ) : null}
            </dl>
            {publicHref && page.isLive ? (
              <Button asChild variant="outline" size="sm" className="w-full">
                <a href={publicHref} target="_blank" rel="noopener noreferrer">
                  <ExternalLink />
                  {t('content.panel.viewLive')}
                </a>
              </Button>
            ) : null}
          </section>

          <section className="space-y-4 rounded-2xl border border-border-default p-4">
            <h2 className="text-sm font-semibold">{t('content.panel.search')}</h2>
            <FormField label={t('content.panel.summary')} hint={t('content.panel.summaryHint')}>
              {(field) => (
                <Textarea
                  {...field}
                  rows={3}
                  maxLength={320}
                  value={form.summary}
                  onChange={(event) => patch({ summary: event.target.value })}
                />
              )}
            </FormField>
            <label className="flex items-start justify-between gap-3 text-sm">
              <span>
                {t('content.panel.noindex')}
                <span className="block text-xs text-text-muted">{t('content.panel.noindexHint')}</span>
              </span>
              <Switch checked={form.noindex} onCheckedChange={(value) => patch({ noindex: value })} />
            </label>
          </section>

          <VersionsPanel
            slug={slug}
            pageVersion={page.version}
            editorDoc={form.doc}
            dirty={dirty}
            onRestored={(next) => {
              accept(next, { reset: true });
              setNotice({ variant: 'success', text: t('content.versions.restored') });
            }}
          />

          <section className="space-y-2 rounded-2xl border border-border-default p-4">
            <h2 className="text-sm font-semibold">{t('content.panel.history')}</h2>
            {history.data?.length ? (
              <ul className="space-y-2 text-sm">
                {history.data.slice(0, 8).map((entry) => (
                  <li key={entry.id} className="flex justify-between gap-2">
                    <span>{t(`content.action.${entry.action.replace('content.', '')}`, { defaultValue: entry.action })}</span>
                    <span className="text-right text-xs text-text-muted">
                      {entry.actorName ? `${entry.actorName} · ` : ''}
                      {formatDateTimeIn(entry.createdAt, locale)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-text-muted">{t('content.panel.noHistory')}</p>
            )}
          </section>

          <section className="space-y-2">
            {page.draft && page.published ? (
              <Button variant="outline" size="sm" className="w-full" onClick={() => setConfirm('discard')}>
                {t('content.editor.discard')}
              </Button>
            ) : null}
            {canPublish && page.isLive && page.kind === 'PAGE' ? (
              <Button variant="outline" size="sm" className="w-full" onClick={() => setConfirm('unpublish')}>
                {t('content.editor.unpublish')}
              </Button>
            ) : null}
            {canPublish && page.kind === 'PAGE' ? (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-danger"
                isLoading={remove.isPending}
                onClick={() => setConfirm('delete')}
              >
                {t('content.editor.delete')}
              </Button>
            ) : null}
          </section>
        </aside>
      </div>

      <Dialog open={confirm !== null} onOpenChange={(next) => !next && setConfirm(null)}>
        <DialogContent>
          {confirm === 'publish' ? (
            <PublishDialog
              nextVersion={nextVersion}
              isAgreement={isAgreement}
              warnings={warnings}
              pending={publish.isPending || schedule.isPending}
              onCancel={() => setConfirm(null)}
              onPublish={() => publish.mutate()}
              onSchedule={(at) => schedule.mutate(at)}
            />
          ) : confirm === 'discard' ? (
            <>
              <DialogHeader>
                <DialogTitle>{t('content.confirm.discardTitle')}</DialogTitle>
                <DialogDescription>{t('content.confirm.discardBody')}</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setConfirm(null)}>
                  {t('common:action.cancel')}
                </Button>
                <Button variant="destructive" isLoading={discard.isPending} onClick={() => discard.mutate()}>
                  {t('content.editor.discard')}
                </Button>
              </DialogFooter>
            </>
          ) : confirm === 'delete' ? (
            <>
              <DialogHeader>
                <DialogTitle>{t('content.confirm.deleteTitle')}</DialogTitle>
                <DialogDescription>{t('content.confirm.deleteBody')}</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setConfirm(null)}>
                  {t('common:action.cancel')}
                </Button>
                <Button variant="destructive" isLoading={remove.isPending} onClick={() => remove.mutate()}>
                  {t('content.editor.delete')}
                </Button>
              </DialogFooter>
            </>
          ) : confirm === 'unpublish' ? (
            <>
              <DialogHeader>
                <DialogTitle>{t('content.confirm.unpublishTitle')}</DialogTitle>
                <DialogDescription>{t('content.confirm.unpublishBody')}</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setConfirm(null)}>
                  {t('common:action.cancel')}
                </Button>
                <Button variant="destructive" isLoading={unpublish.isPending} onClick={() => unpublish.mutate()}>
                  {t('content.editor.unpublish')}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={blocker.state === 'blocked'} onOpenChange={(next) => !next && blocker.reset?.()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('content.confirm.leaveTitle')}</DialogTitle>
            <DialogDescription>{t('content.confirm.leaveBody')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => blocker.reset?.()}>
              {t('content.confirm.stay')}
            </Button>
            <Button variant="destructive" onClick={() => blocker.proceed?.()}>
              {t('content.confirm.leave')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
